// VirtualizedCollectionPage: Darstellungs-Komponente für die Sammlung.
// Rendert ein virtualisiertes Raster von Karten, liest globale Filter aus dem Kontext
// und ruft Daten über React Query ab. Das Routing befindet sich in CollectionRoute.
import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { manaColors, typeOptions } from './uiDefinitions';
import ManaW from '../assets/mana/W.svg';
import ManaU from '../assets/mana/U.svg';
import ManaB from '../assets/mana/B.svg';
import ManaR from '../assets/mana/R.svg';
import ManaG from '../assets/mana/G.svg';
import ManaC from '../assets/mana/C.svg';
import { useFilters } from '../context/FiltersContext';
import { splitCombinedSearch } from '../utils/searchTermSplit';
import { CARD_SORT_OPTIONS, DEFAULT_SORT, naturalDirection } from '../utils/sortOptions';
import { json } from '../api/apiClient';
import SyntaxHelpPopover from '../ui/SyntaxHelpPopover';
import FilterStrip from '../ui/FilterStrip';
import { useGridDimensions } from '../hooks/useGridDimensions';
import { ArrowUp, Download, FileUp, Cloud, SearchX, RefreshCw } from 'lucide-react';
import EmptyState from '../ui/EmptyState';
import Skeleton from '../ui/Skeleton';
import EmptyCollectionGuide from './EmptyCollectionGuide';
import ActionBar from '../ui/ActionBar';
import ContextMenu from '../ui/ContextMenu';
import Page from '../ui/Page';
import CardTile from '../ui/CardTile';

const MANA_FILTER_COLORS = manaColors.map((c) => ({
  code: c.code,
  name: c.name,
  iconSrc: { W: ManaW, U: ManaU, B: ManaB, R: ManaR, G: ManaG, C: ManaC }[c.code],
}));

function VirtualizedCollectionPage({
  authToken,
  handleCardClickInCollection,
  viewedUserId,
  isOwnProfile = true,
  onImportCsv,
  onImportDrive,
}) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const parentRef = useRef(null);
  const cardsPerPage = 30;

  const MIN_COL_WIDTH = 320;
  const GRID_GAP = 6; // == --grid-gutter-dense; gilt horizontal UND vertikal
  const ROW_PADDING = 0; // kein H-Inset → Grid randbündig wie der FilterStrip
  const INFO_HEIGHT_APPROX = 24;

  // Ausgelagerte Logik für Raster-Dimensionen
  const { columns, rowHeight, setRowHeight } = useGridDimensions(
    parentRef,
    MIN_COL_WIDTH,
    GRID_GAP,
    ROW_PADDING,
    INFO_HEIGHT_APPROX
  );

  // Globaler Filter- und Sortier-Status über FiltersContext
  const {
    nameSearchTerm, setNameSearchTerm,
    oracleTextSearchTerm, setOracleTextSearchTerm,
    keywordsSearchTerm, setKeywordsSearchTerm,
    typeSearchTerm, setTypeSearchTerm,
    setSearchTerm, setSetSearchTerm,
    syntaxSearchTerm, setSyntaxSearchTerm,
    selectedColors, toggleColor,
    selectedTypes, setSelectedTypes,
    sourceFilter, setSourceFilter,
    sortField, setSortField,
    sortDirection, setSortDirection,
  } = useFilters();

  // Combined search: merges name + syntax into a single field
  const [combinedSearchValue, setCombinedSearchValue] = useState(() => {
    const parts = [nameSearchTerm, syntaxSearchTerm].filter(Boolean);
    return parts.join(' ');
  });

  const handleCombinedSearchChange = useCallback((val) => {
    setCombinedSearchValue(val);
    const { name, syntax } = splitCombinedSearch(val);
    setNameSearchTerm(name);
    setSyntaxSearchTerm(syntax);
  }, [setNameSearchTerm, setSyntaxSearchTerm]);

  // Debounced values for collection filters
  const [debouncedNameSearchTerm, setDebouncedNameSearchTerm] = useState('');
  const [debouncedOracleTextSearchTerm, setDebouncedOracleTextSearchTerm] = useState('');
  const [debouncedKeywordsSearchTerm, setDebouncedKeywordsSearchTerm] = useState('');
  const [debouncedTypeSearchTerm, setDebouncedTypeSearchTerm] = useState('');
  const [debouncedSyntaxSearchTerm, setDebouncedSyntaxSearchTerm] = useState('');
  const [debouncedSetSearchTerm, setDebouncedSetSearchTerm] = useState('');

  // Debounce effects for text search terms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedNameSearchTerm(nameSearchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [nameSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedOracleTextSearchTerm(oracleTextSearchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [oracleTextSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeywordsSearchTerm(keywordsSearchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [keywordsSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTypeSearchTerm(typeSearchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [typeSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSyntaxSearchTerm(syntaxSearchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [syntaxSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSetSearchTerm(setSearchTerm), 300);
    return () => clearTimeout(handler);
  }, [setSearchTerm]);

  // Build query string for filters
  const getQueryString = useCallback(() => {
    const params = new URLSearchParams();
    params.append('limit', cardsPerPage);
    if (debouncedNameSearchTerm) params.append('nameSearchTerm', debouncedNameSearchTerm);
    if (debouncedOracleTextSearchTerm) params.append('oracleTextSearchTerm', debouncedOracleTextSearchTerm);
    if (debouncedKeywordsSearchTerm) params.append('keywordsSearchTerm', debouncedKeywordsSearchTerm);
    if (debouncedTypeSearchTerm) params.append('typeSearchTerm', debouncedTypeSearchTerm);
    if (debouncedSyntaxSearchTerm) params.append('syntaxSearchTerm', debouncedSyntaxSearchTerm);
    if (debouncedSetSearchTerm) params.append('setSearchTerms', debouncedSetSearchTerm);
    if (selectedColors.length > 0) params.append('colorIdentitySearchTerms', selectedColors.join(','));
    if (selectedTypes.length > 0) params.append('typeLineSearchTerms', selectedTypes.join(','));
    if (sourceFilter === 'binder') params.append('binderType', 'binder');
    if (sourceFilter === 'deck') params.append('binderType', 'deck');
    if (sortField) {
      params.append('sortOption', sortField);
      params.append('sortDirection', sortDirection);
    }
    if (viewedUserId) params.append('userId', viewedUserId);
    return params.toString();
  }, [debouncedNameSearchTerm, debouncedOracleTextSearchTerm, debouncedKeywordsSearchTerm, debouncedTypeSearchTerm, debouncedSyntaxSearchTerm, debouncedSetSearchTerm, selectedColors, selectedTypes, sourceFilter, sortField, sortDirection, viewedUserId]);

  // Infinite Query Hook
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error: queryError,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      'cards',
      debouncedNameSearchTerm,
      debouncedOracleTextSearchTerm,
      debouncedKeywordsSearchTerm,
      debouncedTypeSearchTerm,
      debouncedSyntaxSearchTerm,
      debouncedSetSearchTerm,
      selectedColors,
      selectedTypes,
      sourceFilter,
      sortField,
      sortDirection,
      viewedUserId,
    ],
    queryFn: async ({ pageParam = 0, signal }) => {
      const queryString = getQueryString();
      const url = `/api/cards-data?offset=${pageParam}&${queryString}`;
      try {
        return await json(url, { signal });
      } catch (err) {
        if (err && err.status === 401) {
          logout();
          navigate('/auth', { replace: true });
        }
        throw err;
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage?.totalFilteredCards;
      const loaded = allPages.reduce((acc, p) => acc + (p.cards ? p.cards.length : 0), 0);
      const lastCount = lastPage?.cards ? lastPage.cards.length : 0;
      if (typeof total === 'number') {
        if (loaded >= total || lastCount === 0) return undefined;
        return loaded; // use current loaded count as next offset
      }
      if (!lastPage.cards || lastPage.cards.length < cardsPerPage) {
        return undefined;
      }
      return allPages.length * cardsPerPage;
    },
    initialPageParam: 0,
    enabled: !!authToken,
    staleTime: 1000 * 60 * 5, // 5 minutes - prevent automatic refetch
    gcTime: 1000 * 60 * 10, // 10 minutes - keep data in cache longer
  });

  // Flatten all pages into a single array (memoized for stable effect deps)
  const allCards = useMemo(() => {
    return data?.pages.flatMap(page => page.cards || []) || [];
  }, [data]);

  // Leere Sammlung erkennen (keine Karten UND keine Filter aktiv)
  const hasActiveFilters = !!(
    debouncedNameSearchTerm ||
    debouncedOracleTextSearchTerm ||
    debouncedKeywordsSearchTerm ||
    debouncedTypeSearchTerm ||
    debouncedSyntaxSearchTerm ||
    debouncedSetSearchTerm ||
    selectedColors.length > 0 ||
    selectedTypes.length > 0
  );
  const isEmptyCollection = status === 'success' && allCards.length === 0 && !hasActiveFilters && isOwnProfile;

  // Calibrate row height based on actual card tile after initial render
  useEffect(() => {
    if (!allCards || allCards.length === 0) return;
    const raf = requestAnimationFrame(() => {
      const firstTile = document.querySelector('.card-tile');
      if (firstTile) {
        const measured = firstTile.offsetHeight;
        if (measured && measured > 0) {
          const calibrated = Math.round(measured + GRID_GAP);
          if (Math.abs(calibrated - rowHeight) > 2) {
            setRowHeight(calibrated);
          }
        }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [columns, allCards, rowHeight, setRowHeight]);

  // Virtual Scroller - virtualize ROWS not individual items
  // This is key for multi-column grids to work properly
  const COLUMNS = columns; // dynamic columns based on container width
  const totalRows = Math.ceil((allCards.length + (hasNextPage ? 1 : 0)) / COLUMNS);

  const virtualizer = useWindowVirtualizer({
    count: totalRows,
    estimateSize: () => rowHeight,
    overscan: 4,
  });

  // Wenn sich die geschätzte Zeilenhöhe ändert (z.B. nach erstem Messen), Virtualizer neu berechnen.
  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer, rowHeight, columns]);

  const virtualItems = virtualizer.getVirtualItems();
  // const totalSize = virtualizer.getTotalSize();

  // Source filter options
  const sourceOptions = useMemo(() => ([
    { value: 'all', label: 'Binder + Decks' },
    { value: 'binder', label: 'Nur Binder' },
    { value: 'deck', label: 'Nur Decks' },
  ]), []);

  // Count how many expanded filters have values
  const expandedFilterCount = useMemo(() => {
    let count = 0;
    if (keywordsSearchTerm) count++;
    if (typeSearchTerm) count++;
    if (setSearchTerm) count++;
    return count;
  }, [keywordsSearchTerm, typeSearchTerm, setSearchTerm]);

  // Near-bottom scroll trigger for infinite loading (window-based)
  useEffect(() => {
    const handleScroll = () => {
      if (!hasNextPage || isFetchingNextPage) return;
      const nearBottomThreshold = rowHeight * 2;
      const scrollTop = window.scrollY || window.pageYOffset;
      const clientHeight = window.innerHeight;
      const scrollHeight = document.documentElement.scrollHeight;
      if (scrollTop + clientHeight >= scrollHeight - nearBottomThreshold) {
        fetchNextPage();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasNextPage, isFetchingNextPage, rowHeight, fetchNextPage]);

  // Karten-Zähler für ActionBar
  const totalFilteredCards = typeof data?.pages?.[0]?.totalFilteredCards === 'number'
    ? data.pages[0].totalFilteredCards
    : allCards.length;
  const totalCards = data?.pages?.[0]?.totalCards ?? 0;

  return (
    <Page width="full">
      <div ref={parentRef}>
        {/* ── Seiten-Header (nur eigenes Profil) ── */}
        {isOwnProfile && (
          <ActionBar
            divider
            left={
              <ActionBar.Stat
                id="karten"
                value={`${totalFilteredCards.toLocaleString('de-DE')} / ${totalCards.toLocaleString('de-DE')}`}
                label="Karten"
              />
            }
            right={
              <ContextMenu
                ariaLabel="Sammlung importieren"
                trigger={
                  <ActionBar.Action
                    icon={Download}
                    label="Sammlung importieren"
                    variant="primary"
                    menu
                  />
                }
              >
                <ContextMenu.Item icon={FileUp} onSelect={onImportCsv}>CSV hochladen</ContextMenu.Item>
                <ContextMenu.Item icon={Cloud} onSelect={onImportDrive}>Google Drive Link</ContextMenu.Item>
              </ContextMenu>
            }
          />
        )}

        {/* Filter Container - FilterStrip */}
        <FilterStrip activeExtraCount={expandedFilterCount}>
          <FilterStrip.Search
            value={combinedSearchValue}
            onChange={handleCombinedSearchChange}
            placeholder="Name / Syntax…"
            help={<SyntaxHelpPopover />}
          />
          <FilterStrip.Item id="farbe" active={selectedColors.length > 0}>
            <FilterStrip.ManaColors colors={MANA_FILTER_COLORS} selected={selectedColors} onToggle={toggleColor} />
          </FilterStrip.Item>
          <FilterStrip.Item id="typen" active={selectedTypes.length > 0}>
            <FilterStrip.Group label="Typen">
              <FilterStrip.PillMenu
                mode="multi"
                placeholder="Alle"
                options={typeOptions.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
                value={selectedTypes}
                onChange={setSelectedTypes}
                onClear={() => setSelectedTypes([])}
              />
            </FilterStrip.Group>
          </FilterStrip.Item>
          <FilterStrip.Item id="quelle" active={sourceFilter !== 'all'}>
            <FilterStrip.Group label="Quelle">
              <FilterStrip.PillMenu
                mode="single"
                options={sourceOptions}
                value={sourceFilter}
                onChange={setSourceFilter}
                active={sourceFilter !== 'all'}
              />
            </FilterStrip.Group>
          </FilterStrip.Item>
          <FilterStrip.Item id="regeltext" active={Boolean(oracleTextSearchTerm)}>
            <FilterStrip.Group label="Regeltext">
              <FilterStrip.TextInput value={oracleTextSearchTerm} onChange={(v) => setOracleTextSearchTerm(v)} placeholder="z. B. draw" width={140} />
            </FilterStrip.Group>
          </FilterStrip.Item>
          <FilterStrip.RightGroup>
            <FilterStrip.Item id="sortieren">
              <FilterStrip.Group label="Sortieren">
                <FilterStrip.PillMenu
                  mode="single"
                  options={CARD_SORT_OPTIONS}
                  value={sortField || DEFAULT_SORT.sortOption}
                  onChange={(value) => { setSortField(value); setSortDirection(naturalDirection(value)); }}
                />
                <FilterStrip.SortDirection
                  direction={sortDirection}
                  onClick={() => setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
                />
              </FilterStrip.Group>
            </FilterStrip.Item>
          </FilterStrip.RightGroup>
          <FilterStrip.Extra>
            <FilterStrip.Group label="Set">
              <FilterStrip.TextInput value={setSearchTerm} onChange={(v) => setSetSearchTerm(v)} placeholder="z. B. DSK, MKM" />
            </FilterStrip.Group>
            <FilterStrip.Group label="Keywords">
              <FilterStrip.TextInput value={keywordsSearchTerm} onChange={(v) => setKeywordsSearchTerm(v)} placeholder="z. B. flying, trample" />
            </FilterStrip.Group>
            <FilterStrip.Group label="Subtypen">
              <FilterStrip.TextInput value={typeSearchTerm} onChange={(v) => setTypeSearchTerm(v)} placeholder="z. B. Elf, Goblin" />
            </FilterStrip.Group>
          </FilterStrip.Extra>
        </FilterStrip>

        {status === 'pending' && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_COL_WIDTH}px, 1fr))`,
              gap: `${GRID_GAP}px`,
              marginTop: 'var(--space-md)',
            }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Skeleton width="100%" height="220px" radius="md" />
                <Skeleton width="60%" height="14px" radius="sm" />
              </div>
            ))}
          </div>
        )}

        {status === 'error' && (
          <EmptyState
            variant="error"
            icon={RefreshCw}
            title="Karten konnten nicht geladen werden"
            description={queryError?.message}
            onRetry={() => refetch()}
          />
        )}

        {status === 'success' && allCards.length === 0 && hasActiveFilters && (
          <EmptyState
            variant="filter"
            icon={SearchX}
            title="Keine Karten gefunden"
            description="Passe Suche oder Filter an."
          />
        )}

        {isEmptyCollection && <EmptyCollectionGuide />}

        {allCards.length > 0 && (
          <div
            style={{
              height: 'auto',
              width: '100%',
              position: 'relative',
              marginTop: 'var(--space-md)',
            }}
          >
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              <div>
                {virtualItems.map((virtualRow) => {
                  const startIndex = virtualRow.index * COLUMNS;
                  const endIndex = Math.min(startIndex + COLUMNS, allCards.length + (hasNextPage ? 1 : 0));
                  const rowItems = [];

                  for (let i = startIndex; i < endIndex; i++) {
                    if (i >= allCards.length) {
                      // This is the loader placeholder
                      rowItems.push({ isLoader: true });
                    } else {
                      rowItems.push(allCards[i]);
                    }
                  }

                  return (
                    <div
                      key={virtualRow.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${rowHeight}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                        willChange: 'transform',
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))`,
                          gap: `${GRID_GAP}px`,
                          padding: `${ROW_PADDING}px`,
                          boxSizing: 'border-box',
                          contain: 'layout style paint',
                        }}
                      >
                      {rowItems.map((item, idx) => {
                        if (item.isLoader) {
                          return (
                            <div
                              key={`loader-${virtualRow.index}`}
                              style={{
                                gridColumn: '1 / -1',
                                padding: '10px',
                                textAlign: 'center',
                              }}
                            >
                              {isFetchingNextPage ? 'Lade weitere Karten...' : 'Alle Karten geladen.'}
                            </div>
                          );
                        }

                        const card = item;
                        const uniqueKey = `${card.cardId}-${card.binderName}-${card.quantity}`;
                        // const isInDeck = card.binderType === 'deck';
                        // const deckName = card.binderName;

                        return (
                          <CardTile
                            key={uniqueKey}
                            onClick={() => handleCardClickInCollection(card)}
                            clickLabel={card.name || 'Unbekannte Karte'}
                            scryfallId={card.cardId}
                          >
                            <CardTile.Image
                              src={card.imageUrl}
                              backSrc={card.imageUrlBack && String(card.imageUrlBack).trim() !== '' ? card.imageUrlBack : undefined}
                              alt={card.name || 'Unbekannte Karte'}
                              orientation={card.imageOrientation}
                            />
                            <CardTile.Info>
                              <CardTile.Row>
                                <CardTile.Source variant={card.binderType === 'deck' ? 'deck' : 'binder'}>
                                  {card.binderName}
                                </CardTile.Source>
                                <CardTile.PriceGroup>
                                  <CardTile.Qty>{card.quantity || 1}×</CardTile.Qty>
                                  {card.foil === 'foil' && <CardTile.Foil />}
                                  {card.price !== '' && card.price != null && <CardTile.Price>{card.price} €</CardTile.Price>}
                                </CardTile.PriceGroup>
                              </CardTile.Row>
                            </CardTile.Info>
                          </CardTile>
                        );
                      })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom padding area; loader handled within rows */}
            </div>
          </div>
        )}
      </div>

      {/* ── Scroll to top button ── */}
      <ScrollToTopButton />
    </Page>
  );
}

export default VirtualizedCollectionPage;

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      className="scroll-to-top"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Nach oben scrollen"
      title="Nach oben"
    >
      <ArrowUp size={20} />
    </button>
  );
}


